/* global console */

import axios, {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import * as config from "../../config";
import authStorage from "../storage/auth.storage";
import { RefreshTokenReqBody as RefTokReq, RefreshTokenResBody as RefTokRes } from "../../types/auth.types";
import { BaseResBody } from "../../types/common.types";

export class NetworkService {
  private client: AxiosInstance;

  constructor() {
    const client = axios.create({
      baseURL: config.API_BASE_URL,
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    });

    client.interceptors.request.use(this.requestInterceptor.bind(this), this.requestInterceptorError.bind(this));
    client.interceptors.response.use(this.responseInterceptor.bind(this), this.responseInterceptorError.bind(this));

    this.client = client;
  }

  protected async apiCall<ResBody, ReqBody>(config: AxiosRequestConfig<ReqBody>): Promise<BaseResBody<ResBody>> {
    try {
      const res = await this.client.request<ResBody, AxiosResponse<ResBody, ReqBody>, ReqBody>(config);
      return { type: "success", data: res.data };
    } catch (error) {
      const message: string = error.response?.data?.message || "An unknown error occurred";
      return { type: "failure", message };
    }
  }

  async refreshToken(): Promise<boolean> {
    const token = authStorage.token;
    if (!token) return true;

    const isExpired = Date.now() > token.expires_at;
    if (!isExpired) return false;

    const payload: RefTokReq = {
      refresh_token: token.refresh_token,
      grant_type: "refresh_token",
      client_id: config.CLIENT_ID,
      client_secret: config.CLIENT_SECRET,
    };

    const res = await this.apiCall<RefTokRes, RefTokReq>({
      url: "/oauth/refresh-token",
      method: "POST",
      data: payload,
    });

    if (res.type === "failure") {
      authStorage.removeToken();
      return true;
    }

    authStorage.token = {
      access_token: res.data.access_token,
      refresh_token: token.refresh_token,
      expires_in: Date.now() + res.data.expires_in * 1000,
    };

    return false;
  }

  private async onRefreshToken(requestedUrl: string): Promise<void> {
    const url = "/oauth/refresh-token";
    if (requestedUrl === url) return;

    await this.refreshToken();
  }

  private async requestInterceptor(config: InternalAxiosRequestConfig) {
    await this.onRefreshToken(config.url);

    config.headers = new AxiosHeaders({ ...this.getAxiosHeaders, ...config.headers });

    return config;
  }

  private requestInterceptorError(error: AxiosError): Promise<never> {
    console.error(`[request error] [${JSON.stringify(error)}]`);
    return Promise.reject(error);
  }

  private responseInterceptor(response: AxiosResponse): AxiosResponse {
    return response;
  }

  private responseInterceptorError(error: AxiosError): Promise<never> {
    console.log("response interceptor error:", error);
    const response = error.response;
    const status = response?.status;
    const config = error.config;

    if (error instanceof AxiosError && status === 401 && config) authStorage.removeToken();

    return Promise.reject(error);
  }

  private get getAxiosHeaders(): AxiosHeaders {
    const token = authStorage.token;

    const headers = new AxiosHeaders();
    headers["Content-type"] = "application/json";
    headers["Accept"] = "application/json";
    headers["X-API-Client"] = config.CLIENT_ID;
    headers["X-API-Key"] = config.CLIENT_SECRET;

    if (token) headers.Authorization = `Bearer ${token.access_token}`;

    return headers;
  }
}

export default NetworkService;
